import { AxiosError, AxiosInstance } from "axios";
import {
  CsrfCookieRequestError,
  InvalidCredentialsError,
  AuthenticationFailError,
} from "./errors";
import { PartialUser } from "@/entities/user";
import { SnakeCasedProperties } from "type-fest";

const API_INVALID_DATA_MSG = "The given data was invalid.";

type PartialUserCreate = PartialUser & {
  password: string;
  passwordConfirmation: string;
};

type ApiPartialUser = Omit<
  SnakeCasedProperties<PartialUserCreate>,
  "enabled"
> & { enabled: 1 | 0 };

function partialUserCreateToApi(user: PartialUserCreate): ApiPartialUser {
  return {
    first_name: user.firstName,
    last_name: user.lastName,
    entity_id: user.entityId,
    age: user.age,
    gender_id: user.gender_id,
    role_id: user.roleId,
    email: user.email,
    password: user.password,
    password_confirmation: user.passwordConfirmation,
    enabled: user.enabled ? 1 : 0,
    personal_identification: user.personal_identification,
    township_id: user.township_id,
    urbanization: user.urbanization,
    phone_number: user.phone_number,
    token_grecaptcha: user.token_grecaptcha,
    recaptcha_type: user.recaptcha_type,
    have_disability: user.have_disability,
    ethnic_group_id: user.ethnic_group_id,
  };
}

/**
 * Makes requests to Authenticates the user.
 */
export default class AuthService {
  private _httpClient: AxiosInstance;

  constructor(httpClient: AxiosInstance) {
    this._httpClient = httpClient;
  }
  /**
   * Trys to log in the user with credentials.
   *
   * @param email - The email of the user.
   * @param password - The password of the user.
   * @returns true if the authentication was succesfull
   *
   * @throws CsrfCookieRequestError
   * Thrown if the request to get the csrf cookie fails.
   *
   * @throws InvalidCredentialsError
   * Thrown if the provided credentials where rejected by the API.
   *
   * @throws AuthenticationFailError
   * Thrown if the API doesnt respond with a status code 200 to the login
   * request.
   *
   */
  public async login(
    email: string,
    password: string,
    token: string,
    recaptchaType: "v2" | "v3"
  ): Promise<void> {
    try {
      await this.getCsrfCookie();
      const params = { email, password, token, recaptcha_type: recaptchaType };
      const response = await this._httpClient.post("/login", params);
      if (response.status !== 200) {
        throw new AuthenticationFailError(null);
      }
    } catch (e) {
      const isInvalidCredError =
        (e as AxiosError).isAxiosError &&
        (e as AxiosError).response?.status == 422 &&
        (e as AxiosError<{message: string}>).response?.data?.message === API_INVALID_DATA_MSG;

      if (isInvalidCredError) {
        throw new InvalidCredentialsError(e as Error);
      }
      throw e;
    }
  }

  public async registerUser(user: PartialUserCreate): Promise<void> {
    await this.getCsrfCookie();
    return this._httpClient.post("register", partialUserCreateToApi(user));
  }

  public async forgotPassword(
    email: string,
    token_grecaptcha: string,
    recaptcha_type: string
  ): Promise<void> {
    await this.getCsrfCookie();
    const params = { email, token_grecaptcha, recaptcha_type };
    return this._httpClient.post("forgot-password", params);
  }

  public async resetPassword(
    password: string,
    passwordConfirmation: string,
    email: string,
    token: string,
    token_grecaptcha: string,
    recaptchetype: string
  ): Promise<void> {
    const params = {
      password,
      password_confirmation: passwordConfirmation,
      email,
      token,
      token_grecaptcha,
      recaptcha_type: recaptchetype,
    };

    return this._httpClient.post("reset-password", params);
  }

  public async resendEmail(): Promise<void> {
    //await this.getCsrfCookie();
    return this._httpClient.post("email/verification-notification");
  }

  /**
   * Trys to get the csrf-cookie from the API.
   *
   * @throws CsrfCookieRequestError
   * Thrown if the request to the csrf-cookie endpoints fails.
   */
  public async getCsrfCookie(): Promise<void> {
    try {
      await this._httpClient.get("/sanctum/csrf-cookie");
    } catch (e) {
      throw new CsrfCookieRequestError(e as Error);
    }
  }

  public async getReCaptchaV2SiteKey(): Promise<string> {
    const { data } = await this._httpClient.get<string>(
      "/recaptcha/v2/site-key"
    );
    return data;
  }

  public async getReCaptchaV3SiteKey(): Promise<string> {
    const { data } = await this._httpClient.get<string>(
      "/recaptcha/v3/site-key"
    );
    return data;
  }
}
